import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'

// Coverage configuration
const COBERTURAS_CONFIG = {
	incendioCascoEdificio: {
		nombre: 'Incendio Casco estancia (Edificio)',
		adicionales: ['Granizo', 'HVCT', 'Gastos de remoción de escombros'],
	},
	incendioCascoContenido: {
		nombre: 'Incendio Casco estancia (Contenido)',
		adicionales: ['Granizo', 'HVCT'],
	},
	roboContenidoCasa: {
		nombre: 'Robo Contenido en casa de familia',
		adicionales: [],
	},
	roboValoresCajaFuerte: {
		nombre: 'Robo Valores en Caja Fuerte',
		adicionales: [],
	},
	incendioInstalaciones: {
		nombre: 'Incendio Instalaciones',
		adicionales: ['Daños por acción del agua'],
	},
	incendioInsumosDeposito: {
		nombre: 'Incendio Insumos en depósito',
		adicionales: ['Daños por acción del agua'],
	},
	incendioImplementosAgricolas: {
		nombre: 'Incendio Implementos agrícolas sin propulsión',
		adicionales: ['Daños por acción del agua'],
	},
	incendioMercaderias: {
		nombre: 'Incendio Mercaderías',
		adicionales: ['Combustión espontánea'],
	},
	incendioTanques: {
		nombre: 'Incendio Tanques',
		adicionales: [],
	},
	incendioContenidoTanques: {
		nombre: 'Incendio Contenido Tanques',
		adicionales: [],
	},
	roboMercaderiasInsumos: {
		nombre: 'Robo Mercaderías, Insumos y equipos',
		adicionales: [],
	},
	fidelidadEmpleado: {
		nombre: 'Fidelidad de empleado (Encargado)',
		adicionales: [],
	},
	rcComprensiva: {
		nombre: 'Responsabilidad Civil Comprensiva',
		adicionales: [
			'Animales',
			'Escape y/o estampida de animales',
			'Incendio, rayo, explosión',
			'Otros daños cubiertos',
		],
	},
	equiposElectronicos: {
		nombre: 'Equipos electrónicos en casa de familia',
		adicionales: ['Reposición a nuevo', 'Daños por alta/baja tensión'],
	},
	incendioSilobolsa: {
		nombre: 'Incendio Silobolsa',
		adicionales: ['HVCT'],
	},
} as const

type CoberturaKey = keyof typeof COBERTURAS_CONFIG

interface CoberturaItem {
	enabled: boolean
	sumaAsegurada: string
	adicionales: string[]
}

type CoberturasData = {
	[K in CoberturaKey]?: CoberturaItem
}

interface IntegralAgropecuarioCoberturasScreenProps {
	onNext: (data: {
		coberturas: CoberturasData
		informacionAdicional?: string
	}) => void
	onBack: () => void
	data?: { coberturas?: CoberturasData; informacionAdicional?: string }
}

export function IntegralAgropecuarioCoberturasScreen({
	onNext,
	onBack,
	data,
}: IntegralAgropecuarioCoberturasScreenProps) {
	const [coberturas, setCoberturas] = useState<CoberturasData>(
		data?.coberturas || {},
	)
	const [informacionAdicional, setInformacionAdicional] = useState(
		data?.informacionAdicional || '',
	)
	const [error, setError] = useState<string | null>(null)

	const toggleCobertura = (key: CoberturaKey) => {
		setCoberturas((prev) => {
			const current = prev[key]
			if (current?.enabled) {
				// Disable
				const { [key]: _, ...rest } = prev
				return rest
			} else {
				// Enable
				return {
					...prev,
					[key]: {
						enabled: true,
						sumaAsegurada: current?.sumaAsegurada || '',
						adicionales: current?.adicionales || [],
					},
				}
			}
		})
	}

	const updateSumaAsegurada = (key: CoberturaKey, value: string) => {
		setCoberturas((prev) => ({
			...prev,
			[key]: {
				...prev[key],
				enabled: true,
				sumaAsegurada: value,
				adicionales: prev[key]?.adicionales || [],
			},
		}))
	}

	const toggleAdicional = (key: CoberturaKey, adicional: string) => {
		setCoberturas((prev) => {
			const current = prev[key]
			if (!current) return prev

			const currentAdicionales = current.adicionales || []
			const newAdicionales = currentAdicionales.includes(adicional)
				? currentAdicionales.filter((a) => a !== adicional)
				: [...currentAdicionales, adicional]

			return {
				...prev,
				[key]: {
					...current,
					adicionales: newAdicionales,
				},
			}
		})
	}

	const handleSubmit = () => {
		// Check if at least one coverage is selected
		const hasAnyCoverage = Object.values(coberturas).some((c) => c?.enabled)
		if (!hasAnyCoverage) {
			setError('Selecciona al menos una cobertura')
			return
		}
		setError(null)
		onNext({ coberturas, informacionAdicional })
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-4">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Coberturas
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Selecciona las coberturas que necesitas e indica la suma asegurada
					</p>

					{error && (
						<div className="w-full rounded-lg bg-red-50 p-3 text-sm text-red-600">
							{error}
						</div>
					)}

					<div className="flex w-full flex-col gap-2">
						{(
							Object.entries(COBERTURAS_CONFIG) as [
								CoberturaKey,
								(typeof COBERTURAS_CONFIG)[CoberturaKey],
							][]
						).map(([key, config]) => {
							const cobertura = coberturas[key]
							const isEnabled = cobertura?.enabled || false

							return (
								<div
									key={key}
									className={`rounded-xl border p-3 transition-colors ${
										isEnabled
											? 'border-green-300 bg-green-50'
											: 'border-neutral-200 bg-white'
									}`}
								>
									<div className="flex items-start gap-3">
										<input
											type="checkbox"
											checked={isEnabled}
											onChange={() => toggleCobertura(key)}
											className="mt-1 size-4 rounded border-neutral-300"
										/>
										<div className="flex-1">
											<p className="text-sm font-medium text-neutral-800">
												{config.nombre}
											</p>

											{isEnabled && (
												<div className="mt-2 flex flex-col gap-2">
													<input
														type="text"
														placeholder="Suma asegurada ($)"
														value={cobertura?.sumaAsegurada || ''}
														onChange={(e) =>
															updateSumaAsegurada(key, e.target.value)
														}
														className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
													/>

													{config.adicionales.length > 0 && (
														<div className="flex flex-col gap-1 rounded-lg bg-white/50 p-2">
															<p className="text-xs font-medium text-neutral-600">
																Adicionales:
															</p>
															<div className="flex flex-wrap gap-2">
																{config.adicionales.map((adicional) => (
																	<label
																		key={adicional}
																		className="flex cursor-pointer items-center gap-1.5"
																	>
																		<input
																			type="checkbox"
																			checked={
																				cobertura?.adicionales?.includes(
																					adicional,
																				) || false
																			}
																			onChange={() =>
																				toggleAdicional(key, adicional)
																			}
																			className="size-3 rounded border-neutral-300"
																		/>
																		<span className="text-xs text-neutral-700">
																			{adicional}
																		</span>
																	</label>
																))}
															</div>
														</div>
													)}
												</div>
											)}
										</div>
									</div>
								</div>
							)
						})}
					</div>

					<div className="mt-2 flex w-full flex-col gap-2">
						<label className="text-sm font-medium text-neutral-700">
							Información adicional (opcional)
						</label>
						<textarea
							className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
							rows={3}
							placeholder="Otros detalles o requerimientos especiales..."
							value={informacionAdicional}
							onChange={(e) => setInformacionAdicional(e.target.value)}
						/>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar Cotización
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
